import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Form, Icon, Input } from "semantic-ui-react";

import { createFormHelper } from "lib/Form";
import { PASSWORD_STRENGTH_THRESHOLD } from "modules/Login/components";
import { usePasswordStrength } from "modules/Login/components/ResetPassword/hooks";

import type { ResetPasswordFormPayload } from "modules/Login/components";

type ResetPasswordFormContentProps = {
  errorMsg: string;
  resetHash: string | null;
  isPending: boolean;
  handleSetErrorMsg: (message: string) => void;
  handleSetPasswordStrength: (strength: number | null) => void;
};

const FormHelper = createFormHelper<ResetPasswordFormPayload>();

export const ResetPasswordFormContent = ({ errorMsg, resetHash, isPending, handleSetErrorMsg, handleSetPasswordStrength }: ResetPasswordFormContentProps) => {
  const { t } = useTranslation();

  const [showPassword, setShowPassword] = useState(false);
  const { watch } = FormHelper.useFormContext();
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [newPassword, newPasswordConfirmation] = watch(["newPassword", "newPasswordConfirmation"]);
  const { strength } = usePasswordStrength({ password: newPassword, hash: resetHash || "" });

  useEffect(() => {
    handleSetPasswordStrength(strength);
  }, [strength]);

  const fulfilsPasswordCriteria = !errorMsg && strength === PASSWORD_STRENGTH_THRESHOLD && newPassword === newPasswordConfirmation;

  return (
    <>
      <Form.Field className="password" required error={!!errorMsg}>
        <FormHelper.Field.Input
          name="newPassword"
          inputProps={{
            id: "newPassword",
            icon: "key",
            iconPosition: "left",
            type: showPassword ? "text" : "password",
            placeholder: t("v8_new_password", "New Password"),
            className: `${fulfilsPasswordCriteria ? "valid-password" : ""} ${errorMsg ? "" : "icon-green"}`,
            error: !!errorMsg,
            onChange: () => handleSetErrorMsg("")
          }}
        />
        <Icon
          className={showPassword ? "eye grey password-icon" : "eye slash grey password-icon"}
          onMouseDown={() => setShowPassword(true)}
          onMouseUp={() => setShowPassword(false)}
        />
      </Form.Field>

      <Form.Field className="password" required error={!!errorMsg}>
        <FormHelper.Field.Input
          name="newPasswordConfirmation"
          inputProps={{
            id: "newPasswordConfirmation",
            icon: "key",
            iconPosition: "left",
            type: showConfirmPassword ? "text" : "password",
            placeholder: t("v8_confirm_new_password", "Confirm New Password"),
            className: `${fulfilsPasswordCriteria ? "valid-password" : ""} ${errorMsg ? "" : "icon-green"}`,
            error: !!errorMsg,
            onChange: () => handleSetErrorMsg("")
          }}
        />
        <Icon
          className={showConfirmPassword ? "eye grey password-icon" : "eye slash grey password-icon"}
          onMouseDown={() => setShowConfirmPassword(true)}
          onMouseUp={() => setShowConfirmPassword(false)}
        />
      </Form.Field>
      <div className="mt-25 mb-25">
        <Form.Button disabled={isPending} loading={isPending} type="submit" id="submit-reset-password" color="green" fluid>
          {t("v8_reset_password", "Reset Password")}
        </Form.Button>
      </div>
      {errorMsg && (
        <div className="mb-25">
          <Form.Field error>
            <Input value={errorMsg} readOnly />
          </Form.Field>
        </div>
      )}
    </>
  );
};
